/* -------------------------------------------------------------------------- */
/*                  Componente SlidingPaneDowntimeBinnacle                    */
/* -------------------------------------------------------------------------- */
// Este componente permite ver la bitácora de cualquier entidad

import * as authSelectors from '@redux/reducers/auth';
import { env } from 'env';
import moment from 'moment';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Col, Row } from 'reactstrap';
import { TableColumn } from 'utility/types';
import ReportViewerButton from 'views/components/ReportViewerButton';
import Table from 'views/components/Table';
import Toolbar from 'views/components/Toolbar';
import DateInput from 'views/formComponents/DateInput';
import useGetDowntimeBinnacle from '../../screens/Units/hooks/useGetDowntimeBinnacle';
import * as unitSelectors from '../../screens/Units/reducers';
import { useTheme } from '@src/hooks/useTheme';

//COLUMNAS DE LA TABLA
const columnInfo: TableColumn[] = [
  {
    fieldName: 'startDate',
    headerText: 'units.startDate'
  },
  {
    fieldName: 'endDate',
    headerText: 'units.endDate'
  },
  {
    fieldName: 'startUserName',
    headerText: 'units.startUser'
  },
  {
    fieldName: 'endUserName',
    headerText: 'units.endUser'
  },
  {
    fieldName: 'downtimeReasonDescription',
    headerText: 'units.reason'
  }
];

interface Props {
  unitId?: number | null;
  unitName?: string;
  isOpenSlidingPane: boolean;
  closeSlidingPaneDowntimeBinnacle(): void;
  initialDateRange?: string[];
}

const SlidingPaneDowntimeBinnacle = (props: Props) => {
  const {
    unitId,
    unitName,
    isOpenSlidingPane,
    closeSlidingPaneDowntimeBinnacle,
    initialDateRange = []
  } = props;
  const intl = useIntl();
  const { control, watch, reset } = useForm();
  const formValues = watch();
  const logo = useTheme().app.appLogoImage;

  const user = useSelector(authSelectors.getUser);
  const style = getComputedStyle(document.body);
  const primary = env.VITE_PRIMARY_COLOR;

  const isEditingDowntime = useSelector((state) => unitSelectors.isEditingUnitDowntime(state));

  //CANAL DE COMUNICACION PARA EL VISOR DE REPORTE
  const channelName = 'report_unitDowntimeBinnacle';

  const { downtimeBinnacle, isFetchingDowntimeBinnacle } = useGetDowntimeBinnacle(
    unitId,
    formValues?.dateRange && typeof formValues?.dateRange !== 'string'
      ? formValues?.dateRange[0]
      : undefined,
    formValues?.dateRange && typeof formValues?.dateRange !== 'string'
      ? formValues?.dateRange[1]
      : undefined,
    isEditingDowntime
  );

  const reportDetail = [
    {
      value: downtimeBinnacle.map((binnacle) => ({
        ...binnacle,
        startDate: moment(binnacle.startDate).format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(binnacle.endDate).format('YYYY-MM-DD HH:mm:ss')
      })),
      name: 'reportDetail'
    },
    {
      value: [
        {
          currentDate: moment().startOf('day').format('DD/MM/YYYY'),
          userName: user?.completeName,
          startDateRange: formValues?.dateRange ? formValues?.dateRange[0].substr(0, 10) : '',
          endDateRange: formValues?.dateRange ? formValues?.dateRange[1].substr(0, 10) : '',
          unitName: unitName,
          tableHeaderColor: primary,
          partnerLogo: logo
        }
      ],
      name: 'infoHeaderDataSet'
    }
  ];

  useEffect(() => {
    reset({
      ...formValues,
      dateRange:
        initialDateRange !== undefined &&
        initialDateRange !== null &&
        typeof initialDateRange !== 'string' &&
        initialDateRange.length === 2
          ? [initialDateRange[0], initialDateRange[1]]
          : [
              moment().startOf('day').format('YYYY-MM-DD HH:mm'),
              moment().endOf('day').format('YYYY-MM-DD HH:mm')
            ]
    });
  }, []);

  useEffect(() => {
    if (
      initialDateRange !== undefined &&
      initialDateRange !== null &&
      typeof initialDateRange !== 'string' &&
      initialDateRange.length === 2
    ) {
      reset({
        ...formValues,
        dateRange: [initialDateRange[0], initialDateRange[1]]
      });
    }
  }, [initialDateRange]);

  return unitId && unitName ? (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: 'trackingUnits.downtimeBinnacle' })}
      subtitle={unitName}
      width={'55rem'}
      onRequestClose={() => {
        closeSlidingPaneDowntimeBinnacle();
        reset({
          ...formValues,
          dateRange:
            initialDateRange !== undefined &&
            initialDateRange !== null &&
            typeof initialDateRange !== 'string' &&
            initialDateRange.length === 2
              ? [initialDateRange[0], initialDateRange[1]]
              : [
                  moment().startOf('day').format('YYYY-MM-DD HH:mm'),
                  moment().endOf('day').format('YYYY-MM-DD HH:mm')
                ]
        });
      }}
    >
      <>
        <Row>
          <Col sm="6">
            <Controller
              control={control}
              name="dateRange"
              defaultValue={
                initialDateRange !== undefined &&
                initialDateRange !== null &&
                typeof initialDateRange !== 'string' &&
                initialDateRange.length === 2
                  ? [initialDateRange[0], initialDateRange[1]]
                  : [
                      moment().startOf('day').format('YYYY-MM-DD HH:mm'),
                      moment().endOf('day').format('YYYY-MM-DD HH:mm')
                    ]
              }
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <DateInput
                  field={field}
                  fieldstate={fieldState}
                  maxDate={moment().endOf('day').format('YYYY-MM-DD HH:mm')}
                  isRange
                />
              )}
            />
          </Col>
        </Row>
        <Toolbar
          buttons={[
            <ReportViewerButton
              tooltipMessage={'reports.seeReport'}
              channelName={channelName}
              handleClick={() => {}}
              useEmbbededChannel={true}
              reportPath="UnitDowntimeBinnacle.rdl"
              reportDataSource={{ title: '', dataSets: reportDetail }}
            />
          ]}
        />
        <Table
          id={'tableDowntimeBinnacle'}
          columns={columnInfo}
          isFetching={isFetchingDowntimeBinnacle}
          data={downtimeBinnacle.map((binnacle) => ({
            ...binnacle,
            startDate: moment(binnacle.startDate).format('YYYY-MM-DD HH:mm:ss'),
            endDate: binnacle.endDate
              ? moment(binnacle.endDate).format('YYYY-MM-DD HH:mm:ss')
              : intl.formatMessage({ id: 'units.inProgress' })
          }))}
          enablePersistence={false}
        />
      </>
    </SlidingPane>
  ) : null;
};

SlidingPaneDowntimeBinnacle.defaultProps = {};

export default SlidingPaneDowntimeBinnacle;
